import {notEmpty} from '@wandb/weave/common/util/obj';
import React, {FC, useCallback, useEffect, useState} from 'react';
// @todo: Migrate to 'Link' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Link, useHistory} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button, Dropdown, Modal} from 'semantic-ui-react';

import {useInviteQuery, useUpdateUserMutation} from '../../generated/graphql';
import {auth} from '../../setup';
import {useViewer} from '../../state/viewer/hooks';
import {setAuthRedirect} from '../../util/auth';
import {propagateErrorsContext} from '../../util/errors';
import * as S from './TeamInviteModal.styles';
import {useRedirectToSignupIfFollowingInviteLinkAndNotLoggedIn} from './utils';

interface TeamInviteModalProps {
  inviteQueryParam: string;
  isNewUser: boolean;
  isNewTeam: boolean;
  setInviteQueryParam: (queryParam: string) => void;
  teamName: string;
}

const TeamInviteModal: FC<TeamInviteModalProps> = React.memo(
  ({inviteQueryParam, isNewUser, isNewTeam, setInviteQueryParam, teamName}) => {
    const viewer = useViewer();
    const history = useHistory();
    const [defaultEntity, setDefaultEntity] = useState<string>('');
    const [updatingUser, setUpdatingUser] = useState<boolean>(false);
    const [toUsername, setToUsername] = useState<string>('');
    const [unauthorizedInvite, setUnauthorizedInvite] =
      useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [updateUser] = useUpdateUserMutation({
      context: propagateErrorsContext(),
    });

    // check if the invitation is sent to the same user as viewer
    const inviteResult = useInviteQuery({
      variables: {key: inviteQueryParam},
      fetchPolicy: 'cache-and-network',
    });

    const onClose = useCallback(() => {
      setShowModal(false);

      setInviteQueryParam('');
      // note: this erases all querystring params
      const url: string = document.location.pathname;
      history.push(url);
    }, [setInviteQueryParam, history]);

    useEffect(() => {
      // initialize defaultEntity to team
      if (defaultEntity === '') {
        setDefaultEntity(teamName);
      }
    }, [defaultEntity, teamName]);

    useEffect(() => {
      if ((inviteResult.data != null || isNewTeam) && !isNewUser) {
        setShowModal(true);
        window.analytics?.track('Update Entity Modal Viewed');
      }
    }, [inviteResult, isNewTeam, isNewUser]);

    useRedirectToSignupIfFollowingInviteLinkAndNotLoggedIn({
      isInviteToNewUser:
        inviteResult.data?.invite != null &&
        inviteResult.data.invite.toUser == null,
      isViewerLoggedIn: viewer != null,
    });

    useEffect(() => {
      const maybeCloseModalOrUpdateDefaultEntity = async () => {
        if (showModal) {
          // Keep modal open if current user just created the team (indicated by `isNewTeam`)
          if (isNewTeam) {
            return;
          }
          if (inviteResult.data?.invite?.toUser == null) {
            if (viewer != null) {
              // User followed invite link sent to a non-user email address, but user is already logged in -> close modal
              onClose();
            }
          } else {
            // Invitation is to an existing user; check if invitation is sent to the right user
            const inviteeUsername = inviteResult.data.invite.toUser.username;
            if (inviteeUsername == null) {
              console.error(
                'ERROR: invite is to existing user, but invitee username is null'
              );
              return;
            }
            if (viewer == null) {
              return;
            }
            setToUsername(inviteeUsername);
            if (viewer.username !== inviteeUsername) {
              setUnauthorizedInvite(true);
            }
          }
        }
        // If new user, we want to automatically update default entity to the team
        if (isNewUser && viewer != null) {
          setUpdatingUser(true);
          try {
            await updateUser({
              variables: {defaultEntity: teamName},
            });
            onClose();
          } catch (e) {
            console.error('ERROR UPDATING DEFAULT ENTITY', e);
          } finally {
            setUpdatingUser(false);
          }
        }
      };
      maybeCloseModalOrUpdateDefaultEntity();
    }, [
      inviteResult,
      viewer,
      showModal,
      onClose,
      isNewUser,
      isNewTeam,
      teamName,
      updateUser,
    ]);

    // team options for default entity picker
    const teamEdges = viewer?.teams?.edges.filter(notEmpty);
    let teamOptions = teamEdges?.map(e => ({
      text: e?.node?.name,
      value: e?.node?.name,
    }));
    const shouldHidePersonalEntity =
      viewer?.userInfo?.isPersonalEntityHidden ?? false;
    if (shouldHidePersonalEntity && teamOptions) {
      teamOptions = teamOptions.filter(
        t => viewer?.username && viewer.username !== t.value
      );
    }

    return (
      <>
        {/* VALID INVITATION -> SHOW DEFAULT ENTITY PICKER MODAL */}
        <Modal
          size={'tiny'}
          open={showModal && !unauthorizedInvite}
          data-test="new-team-popup">
          <Modal.Header>Sending runs to this team</Modal.Header>
          <Modal.Content>
            <S.ContentWrapper>
              <S.Inline>
                Now that you're part of this team, we recommend updating this
                setting so your new runs go to the team automatically, instead
                of to your personal account.
              </S.Inline>
              <S.Inline>
                Set your default destination for new runs:
                {teamOptions != null && (
                  <S.DropdownWrapper>
                    <Dropdown
                      selection
                      fluid
                      options={teamOptions}
                      value={defaultEntity}
                      onChange={(e, {value}) => {
                        setDefaultEntity(value as string);
                      }}
                    />
                  </S.DropdownWrapper>
                )}
              </S.Inline>
              <S.Inline>
                You can change this later in your{' '}
                <Link to="/settings">account settings</Link>.
              </S.Inline>
              <S.ButtonWrapper>
                <Button onClick={onClose}>Close</Button>
                <Button
                  primary
                  loading={updatingUser}
                  disabled={updatingUser}
                  onClick={async () => {
                    window.analytics?.track('Default Entity Updated', {
                      entityName: defaultEntity,
                      location: 'default entity modal',
                    });
                    setUpdatingUser(true);
                    try {
                      updateUser({
                        variables: {defaultEntity},
                      });
                      onClose();
                    } catch (e) {
                      console.error('ERROR UPDATING DEFAULT ENTITY', e);
                    } finally {
                      setUpdatingUser(false);
                    }
                  }}>
                  Update
                </Button>
              </S.ButtonWrapper>
            </S.ContentWrapper>
          </Modal.Content>
        </Modal>
        {/* INVALID INVITATION -> SHOW WRONG USER MODAL */}
        <Modal size={'tiny'} open={showModal && unauthorizedInvite}>
          <Modal.Header>Incorrect account</Modal.Header>
          <Modal.Content>
            <S.ContentWrapper>
              <S.Inline>
                The <S.Bold>{teamName}</S.Bold> invitation was sent to username{' '}
                <S.Bold>{toUsername}</S.Bold>, but you're logged in as{' '}
                <S.Bold>{viewer?.username}</S.Bold>. Please log back in with
                your other account to accept this invitation.
              </S.Inline>
              <S.ButtonWrapper>
                <Button
                  primary
                  onClick={() => {
                    setAuthRedirect(document.location.href);
                    auth.logout();
                  }}>
                  Log in as {toUsername}
                </Button>
              </S.ButtonWrapper>
            </S.ContentWrapper>
          </Modal.Content>
        </Modal>
      </>
    );
  }
);

export default TeamInviteModal;
